// Every model call WINDTUNNEL makes, kept where a reviewer can read it.
//
// The design record (`src/ui/design/DesignRecord.tsx`) claims a specific discipline about the LLM
// (large language model): it proposes, the deterministic layer vets, the human commits; citations are
// checked against real event ids; illegal orders are dropped with a reason; a slow model degrades to a
// template rather than blocking. That claim is worth no more than the evidence for it, so this module
// keeps the evidence: an in-memory ring buffer of the last 200 calls with what was asked, what came
// back, how long it took, and what validation threw away.
//
// It is deliberately in memory only (no storage, no network): it is a debugging and demonstration
// surface for the run you are in, not telemetry. It drops when the tab does.
//
// Wiring: `src/ai/client.ts` opens a row for every `callLLM` and closes it when the call settles;
// `src/ai/validate.ts` attaches citation results for calls whose returned object it validates; the
// advisor, FRAGO and debrief call sites attach their own validation results with `note()`, because
// they validate copies of the answer rather than the answer itself.

/**
 * The jobs a model does in this product (spec §6, `docs/design/ai-patterns.md`).
 *
 * The three `jev:` purposes are the S-2 estimate questions asked of TypeSafe AI's System One model
 * behind the instructor toggle (`src/ai/estimator.ts`). They are spelled out as three literals rather
 * than a template type so `PURPOSE_LABEL` stays an exhaustive `Record` and every existing reader of it
 * keeps type-checking.
 */
export type AuditPurpose =
  | 'advisor' | 's2' | 'sitrep' | 'frago' | 'debrief' | 'other'
  | 'jev:mainEffortThird' | 'jev:contactRole' | 'jev:reserveCommitSoon';

export const PURPOSE_LABEL: Record<AuditPurpose, string> = {
  advisor: 'Advisor play',
  s2: 'S-2 assessment',
  sitrep: 'SITREP phrasing',
  frago: 'FRAGO parse',
  debrief: 'Debrief narrative',
  other: 'Other',
  'jev:mainEffortThird': 'S-2 estimate: main effort',
  'jev:contactRole': 'S-2 estimate: contact role',
  'jev:reserveCommitSoon': 'S-2 estimate: reserve commitment',
};

/** The model ids a row can name. `jev-latest` is TypeSafe's System One route; it is not an LLM and it
 * writes no text, which is why it is a separate value rather than a third Anthropic tier. */
export type AuditModel = 'sonnet' | 'haiku' | 'jev-latest';

export const MODEL_LABEL: Record<AuditModel, string> = {
  sonnet: 'claude-sonnet-5',
  haiku: 'claude-haiku-4-5',
  'jev-latest': 'jev-latest',
};

export interface AuditEntry {
  id: number;
  /** Wall-clock time the call was made. */
  at: number;
  purpose: AuditPurpose;
  /** Which advisor, which event, which play - whatever names this particular call. */
  ref?: string;
  model: AuditModel;
  /** Estimate only: prompt characters / 4, the usual rule of thumb. Nothing here is billed. */
  promptTokens: number;
  /** Milliseconds from the call leaving `callLLM` to it resolving, template fallback included. */
  latencyMs: number;
  /**
   * Milliseconds to the first characters of a streamed answer, when the call streamed.
   *
   * The number a reader actually experiences. `latencyMs` is when the answer could be validated and
   * acted on; this is when the card stopped being a wait and started being something to read, and the
   * gap between the two is the whole of what streaming buys.
   */
  firstTextMs?: number;
  /** Still out. A row with no source is a call the page is waiting on. */
  source?: 'llm' | 'template' | 'cached';
  /** Why the model answer was not used, when it was not. */
  error?: string;
  /**
   * `usage.cache_read_input_tokens` from the provider: how much of this prompt was served out of
   * Anthropic's prompt cache rather than read afresh.
   *
   * It is the receipt for a specific design claim. The advisors' system block is built to be
   * byte-identical for a whole run (`src/ai/prompts.ts`) and marked `cache_control: ephemeral` by the
   * proxy; if that worked, every advisor call after the first in a five-minute window reports a
   * non-zero number here. The independent review's "prompt caching inert" finding was unfalsifiable
   * until this column existed - a cache that does not engage fails silently and costs full price.
   */
  cachedTokens?: number;
  /** Citation grounding (`validateCitations`): how many ids the model gave, how many did not exist. */
  citedTotal?: number;
  citedDropped?: number;
  /** More than half the cited ids were invalid, so the whole message is flagged in the interface. */
  flagged?: boolean;
  /** Order legality (`validatePlay`): how many orders survived, and the reason each rejection gave. */
  ordersKept?: number;
  ordersRejected?: string[];

  // ---- S-2 estimate rows (`jev:` purposes) --------------------------------------------------------
  //
  // An estimate is not a text answer, so the columns that describe one are different: which provider
  // actually answered (a Jev call that times out is served by the heuristic, and the row must say so
  // rather than quietly claiming a model answer), how concentrated the returned distribution was, and
  // whether a fitted calibration map was applied to it. Without the last of those, "62% · Jev" on a
  // card is a number nobody can audit.

  /** Which provider produced the answer on this row, after any fallback. */
  provider?: 'heuristic' | 'jev';
  /** The answer's confidence, 0..1 — TypeSafe's own for a choice, synthesised for a yes/no. */
  confidence?: number;
  /** Whether a fitted map from `data/jev-calibration.json` was applied to the probabilities. */
  calibrated?: boolean;
}

const LIMIT = 200;

let nextId = 1;
const entries: AuditEntry[] = [];
const listeners = new Set<() => void>();

/** A new array identity per change, so `useSyncExternalStore` sees the change without a deep compare. */
let snapshot: readonly AuditEntry[] = [];

function publish(): void {
  snapshot = entries.slice().reverse(); // newest first, which is the only order the table wants
  for (const fn of listeners) fn();
}

/** Opens a row for a call that has just gone out. Returns the id to close it with. */
export function record(init: {
  purpose: AuditPurpose;
  model: AuditModel;
  promptChars: number;
  ref?: string;
}): number {
  const entry: AuditEntry = {
    id: nextId++,
    at: Date.now(),
    purpose: init.purpose,
    ref: init.ref,
    model: init.model,
    promptTokens: Math.round(init.promptChars / 4),
    latencyMs: 0,
  };
  entries.push(entry);
  if (entries.length > LIMIT) entries.splice(0, entries.length - LIMIT);
  publish();
  return entry.id;
}

/** Merges anything later known about a call into its row. Unknown ids are ignored (the ring dropped it). */
export function note(id: number | undefined, patch: Partial<Omit<AuditEntry, 'id' | 'at'>>): void {
  if (id === undefined) return;
  const entry = entries.find((e) => e.id === id);
  if (!entry) return;
  Object.assign(entry, patch);
  publish();
}

export function entriesNewestFirst(): readonly AuditEntry[] {
  return snapshot;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function clear(): void {
  entries.length = 0;
  publish();
}

// ---- Correlating a validation back to the call that produced it ----------------------------------
//
// `validateCitations` is called by the report feed on the exact object `callLLM` returned, in a store
// this work does not own. Rather than thread an id through that call site, the client tags the answer
// object itself here and the validator looks it up: an identity match, so two assessments in flight at
// once cannot be confused with each other. Answers that are copied or unwrapped before validation (the
// advisors' plays, the debrief's paragraphs) simply miss, and those call sites call `note()` directly.

const byValue = new WeakMap<object, number>();

/** Tags a model answer with the audit row it came from. Non-objects are ignored. */
export function tag(value: unknown, id: number): void {
  if (value && typeof value === 'object') byValue.set(value as object, id);
}

/** The audit row a model answer came from, if it is still tagged. */
export function idOf(value: unknown): number | undefined {
  return value && typeof value === 'object' ? byValue.get(value as object) : undefined;
}
